import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { doGetPromise } from '../../../core/config/core.httpclient-utils';
import { DiffAggregateService } from './scm.diff.service';

@Injectable()
export class ScmService {

  private baseUrl = '/api/scm/';

  constructor(private http: HttpClient, private diffAggregateService: DiffAggregateService) { }

  save(unixName: string, body: any): Promise<any> {
    return this.http.post(`${this.baseUrl}${unixName}`, body).toPromise();
  }

  fetch(unixName: string, module?: string, queryParams?: any): Promise<any> {
    return doGetPromise(this.http, `${this.baseUrl}${unixName}/${module}`, queryParams);
  }

  summary(unixName: string, queryParams?: any): Promise<any> {
    return doGetPromise(this.http, `${this.baseUrl}${unixName}/summary`, queryParams);
  }

  commits(unixName: string): Promise<any> {
    return this.http.get(`${this.baseUrl}${unixName}/commit`).toPromise();
  }

  tags(unixName: string, queryParams?: any): Promise<any> {
    return doGetPromise(this.http, `${this.baseUrl}${unixName}/tag`, queryParams);
  }

  promotionModel(unixName: string): Promise<any> {
    return this.http.get(`${this.baseUrl}${unixName}/promotionModel`).toPromise();
  }

  savePromotionModel(unixName: string, params: any): Promise<any> {
    return this.http.post(`${this.baseUrl}${unixName}/promotionModel`, params).toPromise();
  }

  branches(unixName: string, queryParams?: any): Promise<any> {
    return doGetPromise(this.http, `${this.baseUrl}${unixName}/branch`, queryParams);
  }

  branch(unixName: string, branch: string): Promise<any> {
    return this.http.get(`${this.baseUrl}${unixName}/branch/${branch}`).toPromise();
  }

  tree(unixName: string, queryParams?: any): Promise<any> {
    return doGetPromise(this.http, `${this.baseUrl}${unixName}/tree`, queryParams);
  }

  commitDiff(unixName: string, commit: string, queryParams?: any): Promise<any> {
    return doGetPromise(this.http, `${this.baseUrl}${unixName}/commit/${commit}`, queryParams)
      .then((data: any) => {
        const processedData = this.diffAggregateService.addDiffTotals(
          this.diffAggregateService.addDiffComments(data)
        );
        return processedData;
      });
  }

  fileVersion(unixName: string, path: string, queryParams?: any): Promise<any> {
    return doGetPromise(this.http, `${this.baseUrl}${unixName}/file/${path}`, queryParams);
  }

  merge(unixName: string, queryParams?: any): Promise<any> {
    return doGetPromise(this.http, `${this.baseUrl}${unixName}/merge`, queryParams)
      .then((data: any) => {
        const processedData = this.diffAggregateService.addDiffTotals(
          this.diffAggregateService.addDiffComments(data)
        );
        return processedData;
      });
  }

  saveMerge(unixName: string, params: any): Promise<any> {
    return this.http.post(`${this.baseUrl}${unixName}/merge`, params).pipe(
      map((data: any) => this.diffAggregateService.addDiffTotals(
        this.diffAggregateService.addDiffComments(data))
      )
    ).toPromise();
  }

  searchBranch(unixName: string, branchSubStr: string): Promise<any> {
    return doGetPromise(this.http, `${this.baseUrl}${unixName}/branch`, { filter: branchSubStr });
  }

  postRepo(unixName: string, hash: string, body: any): Promise<any> {
    return this.http.post(`${this.baseUrl}${unixName}/repo/${hash}`, body).toPromise();
  }
}
